import { IconButton, Tooltip, Typography } from "@mui/material"
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { DocumentClosingForm } from "../../../../../models/document-closing";
import { Document, DocumentAdd, DocumentEdit } from "../../../../../models/document";
import SelectInvoices from "../select-invoices/SelectInvoices";
import { padWithZeros } from "../../../../../helpers/helpers";
import { Account } from "../../../../../models/account";
import { DocumentSettings } from "../../../../../models/document-settings";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import { useTheme } from "@emotion/react";
import ClearIcon from '@mui/icons-material/Clear';
import { DocumentPaymentForm } from "../../../../../models/document-payment";
import { calcKabala } from "../../../../../services/documentsService";

//state properties:
interface LocationState {
    document_account?: Account | null;
}

interface InvoicesProps {

    method: "add" | "edit";

    documentForm: UseFormReturn<DocumentAdd> | UseFormReturn<DocumentEdit>;
    paymentsForm: UseFormReturn<{ payments: DocumentPaymentForm[] }>
    documentClosingForm: UseFormReturn<{ documents: DocumentClosingForm[] }>;
    documentsClosingFieldArray: UseFieldArrayReturn<{ documents: DocumentClosingForm[] }, "documents", "id">;

    settings: DocumentSettings | undefined;

    setDocumentsToClose: (selectedDocuments: Document[]) => void;

    documentsToClose?: Document[];
    isDocumentClosed?: boolean;
}

function Invoices({ method, documentForm, paymentsForm, documentClosingForm, documentsClosingFieldArray, settings, documentsToClose=[], setDocumentsToClose, isDocumentClosed }: InvoicesProps) {

    const theme = useTheme();
    const { state } = useLocation() as { state: LocationState }; // state data from another page

    const [isSelectInvoicesDialogOpen, setIsSelectInvoicesDialogOpen] = useState<boolean>(false); // set if open select invoices dialog

    // actions after getting the document settings.
    useEffect(() => {
        if (settings) {

            // show select invoices in kabala if needed.
            if (settings.name === 'קבלה' && state?.document_account) {
                setIsSelectInvoicesDialogOpen(true);
            }
        }
    }, [settings]);

    //deleting a row by specify an index and row type
    function deleteRow(index: number): void {

        documentsClosingFieldArray.remove(index);
        const updatedDocumentsToClose: Document[] = documentsToClose.filter((_, ind)=> ind !== index );
        setDocumentsToClose(updatedDocumentsToClose);
        const payments = paymentsForm.getValues().payments;
        calcKabala(documentForm, payments, settings, updatedDocumentsToClose);
        
    }

    if(method === "add"){

        documentForm = documentForm as UseFormReturn<DocumentAdd>;

        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span">
                            בחירת חשבוניות לתשלום:
                        </Typography>
                        <Tooltip title="בחירת חשבוניות לתשלום" placement="top">
                            <IconButton onClick={() => { setIsSelectInvoicesDialogOpen(true) }} disabled={!documentForm.getValues('document_account.account_number') } sx={{ color: theme.colors.main_color }}>
                                <AutoStoriesIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th>מסמך</th>
                            <th>יתרה</th>
                            <th>יתרה חדשה</th>
                            <th>סטטוס</th>
                            <th>סטטוס חדש</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {documentsClosingFieldArray.fields.map((document, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="td-text">
                                    
                                    {rowIndex + 1}.
                                    
                                </td>
                                <td className="td-text">{document.name}</td>
                                <td className="td-text">{document.old_balance}₪</td>
                                <td className="td-text">{document.balance}₪</td>
                                <td className="td-text">{document.old_status}</td>
                                <td className="td-text">{document.status}</td>
                                <td className="delete-row">
                                    <Tooltip title="הסרת שורה" placement="top">
                                        <span>
                                            <IconButton onClick={()=>{deleteRow(rowIndex)}} sx={{ color: theme.colors.main_color }}>
                                                <ClearIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
    
    
                <SelectInvoices
    
                    open={isSelectInvoicesDialogOpen}
                    onClose={(selectedDocuments?: Document[]) => {
    
                        setIsSelectInvoicesDialogOpen(false);
    
                        if (selectedDocuments) {
                            setDocumentsToClose(selectedDocuments);
    
                            const formToUpadte: DocumentClosingForm[] = selectedDocuments.map(document => ({
                                ...document,
                                name: `${document.document_settings?.name} ${document.document_number}`,
                                document_closed_id: document.id,
                                old_status: document.status,
                                balance: document.balance!,
                                old_balance: document.balance!,
                                amount: document.balance!,
                                reference: `${document.document_settings?.short_name} ${padWithZeros(document.document_number, 7)}`
                            }));
    
                            documentClosingForm.reset({ documents: formToUpadte });
                            const payments = paymentsForm.getValues().payments;
                            calcKabala(documentForm, payments, settings, selectedDocuments);
                        }
    
                    }}
                    accountId={documentForm.getValues("document_account.account_id")}
                    documentsSelected={documentsToClose}
                />
    
            </>
        )
    }

    else if(method === "edit"){
        
        documentForm = documentForm as UseFormReturn<DocumentEdit>;

        return (
            <>
                <div className={`title ${isDocumentClosed? "disabled" : ""}`}>
                    <div>
                        <Typography variant="body1" component="span">
                            בחירת חשבוניות לתשלום:
                        </Typography>
                        <Tooltip title="בחירת חשבוניות לתשלום" placement="top">
                            <IconButton onClick={() => { setIsSelectInvoicesDialogOpen(true) }} disabled={!documentForm.getValues('document_account.account_number') || isDocumentClosed } sx={{ color: theme.colors.main_color }}>
                                <AutoStoriesIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
                <table className={isDocumentClosed? "disabled" : ""}>
                    <thead>
                        <tr>
                            <th>מסמך</th>
                            <th>יתרה</th>
                            <th>יתרה חדשה</th>
                            <th>סטטוס</th>
                            <th>סטטוס חדש</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {documentsClosingFieldArray.fields.map((document, rowIndex) => (
                            <tr key={rowIndex}>
                                <td>{document.name}</td>
                                <td>{document.old_balance}₪</td>
                                <td>{document.balance}₪</td>
                                <td>{document.old_status}</td>
                                <td>{document.status}</td>
                                <td className="delete-row">
                                    <Tooltip title="הסרת שורה" placement="top">
                                        <span>
                                            <IconButton onClick={()=>{deleteRow(rowIndex)}} sx={{ color: theme.colors.main_color }} disabled={isDocumentClosed}>
                                                <ClearIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
    
    
                <SelectInvoices
    
                    open={isSelectInvoicesDialogOpen}
                    onClose={(selectedDocuments?: Document[]) => {
    
                        setIsSelectInvoicesDialogOpen(false);
    
                        if (selectedDocuments) {
                            setDocumentsToClose(selectedDocuments);
    
                            const formToUpadte: DocumentClosingForm[] = selectedDocuments.map(document => ({
                                ...document,
                                name: `${document.document_settings?.name} ${document.document_number}`,
                                document_closed_id: document.id,
                                old_status: document.status,
                                balance: document.balance!,
                                old_balance: document.balance!,
                                amount: document.balance!,
                                reference: `${document.document_settings?.short_name} ${padWithZeros(document.document_number, 7)}`
                            }));
    
                            documentClosingForm.reset({ documents: formToUpadte });
                            const payments = paymentsForm.getValues().payments;
                            calcKabala(documentForm, payments, settings, selectedDocuments);

                        }
    
                    }}
                    accountId={documentForm.getValues("document_account.id")}
                    documentsSelected={documentsToClose}
                />
    
            </>
        )
    }
    
}

export default Invoices